import { createReducer, on } from '@ngrx/store';
import * as TemplateActions from './template.actions';
import * as AuthActions from '../auth/auth.actions';
import { CustomTemplate } from '../../models/CustomTemplate.model';
import { CustomSlug } from '../../models/CustomSlug.model';
import { CustomComponent } from '../../models/CustomComponent.model';
import { AuthState } from '../auth/auth.state';
import { DataState } from '../data/data.reducer';
import { StatePersistenceUtil } from '../../utils/state-persistence.util';

export interface TemplateState {
  currentMode: 1 | 2 | 3;
  slugs: {
    [key: string]: {
      staticTemplate: CustomTemplate | undefined;
      dynamicTemplate: CustomTemplate | undefined;
      customSlug: CustomSlug | undefined;
      loaded: boolean;
      lazyLoaded: boolean;
      lazyLoadedData: any;
    }
  };
  previousStaticTemplateUuid: string | null;
  previousDynamicTemplateUuid: string | null;
  error: any;
  isHydrated: boolean;
}

// Helper function to reconstruct CustomTemplate instances
const reconstructCustomTemplate = (templateData: any): CustomTemplate | undefined => {
  if (!templateData) return undefined;
  
  // Reconstruct CustomComponent instances
  const reconstructedComponents = templateData.customComponents?.map((compData: any) => {
    // Reconstruct child components recursively
    const reconstructedChildComponents = compData.childComponents?.map((childData: any) => 
      new CustomComponent(
        childData.name,
        childData.x,
        childData.y,
        childData.cols,
        childData.rows,
        childData.customCSS,
        childData.settings || {},
        childData.inputs || {},
        childData.mode,
        childData.uuid,
        childData.childComponents // Will be reconstructed recursively if needed
      )
    ) || [];
    
    return new CustomComponent(
      compData.name,
      compData.x,
      compData.y,
      compData.cols,
      compData.rows,
      compData.customCSS,
      compData.settings || {},
      compData.inputs || {},
      compData.mode,
      compData.uuid,
      reconstructedChildComponents
    );
  }) || [];
  
  // Reconstruct CustomTemplate instance
  return new CustomTemplate(
    templateData.name,
    templateData.isstatic,
    reconstructedComponents,
    templateData.id,
    templateData.uuid
  );
};

// Load initial state from localStorage if available
const loadInitialState = (): TemplateState => {
  const baseState: TemplateState = {
    currentMode: 1,
    slugs: {},
    previousStaticTemplateUuid: null,
    previousDynamicTemplateUuid: null,
    error: null,
    isHydrated: false
  };

  try {
    const persistedState = StatePersistenceUtil.loadTemplateState();
    if (persistedState) {
      
      // Reconstruct CustomSlug instances and CustomTemplate instances from persisted data
      const reconstructedSlugs: any = {};
      if (persistedState.slugs) {
        Object.entries(persistedState.slugs).forEach(([slug, slugData]: [string, any]) => {
          reconstructedSlugs[slug] = {
            ...slugData,
            customSlug: slugData.customSlug 
              ? new CustomSlug(
                  slugData.customSlug.slug,
                  slugData.customSlug.static_template_uuid,
                  slugData.customSlug.dynamic_template_uuid,
                  slugData.customSlug.id,
                  slugData.customSlug.uuid,
                  slugData.customSlug.isDevelopment
                )
              : undefined,
            staticTemplate: reconstructCustomTemplate(slugData.staticTemplate),
            dynamicTemplate: reconstructCustomTemplate(slugData.dynamicTemplate)
          };
        });
      }
      
      return {
        ...baseState,
        ...persistedState,
        slugs: reconstructedSlugs,
        isHydrated: true
      };
    }
  } catch (error) {
    console.error('[TemplateReducer] Error loading persisted state:', error);
    StatePersistenceUtil.clearStoredState();
  }

  return baseState;
};

const initialState: TemplateState = loadInitialState();

// Helper function to save state to localStorage
const saveStateToStorage = (state: TemplateState): void => {
  try {
    // Only save if we have loaded templates and state is hydrated
    if (state.isHydrated && Object.keys(state.slugs).some(slug => state.slugs[slug].loaded)) {
      StatePersistenceUtil.saveTemplateState(state);
    }
  } catch (error) {
    console.error('[TemplateReducer] Error saving state to localStorage:', error);
  }
};

export const templateReducer = createReducer(
  initialState,
  on(AuthActions.resetAllState, () => {
    StatePersistenceUtil.clearStoredState();
    return {
      ...loadInitialState(),
      slugs: {}, // Clear all cached data on auth reset
      isHydrated: true
    };
  }),
  on(TemplateActions.loadStaticTemplateSuccess, (state, { slug, staticTemplate }) => {
    const existingSlugData = state.slugs[slug] || {
      staticTemplate: undefined,
      dynamicTemplate: undefined,
      customSlug: undefined,
      loaded: false,
      lazyLoaded: false,
      lazyLoadedData: null
    };

    // Ensure template is properly reconstructed if needed
    const reconstructedStaticTemplate = staticTemplate instanceof CustomTemplate 
      ? staticTemplate 
      : reconstructCustomTemplate(staticTemplate);

    const newState = {
      ...state,
      slugs: {
        ...state.slugs,
        [slug]: { 
          ...existingSlugData,
          staticTemplate: reconstructedStaticTemplate
        }
      },
      previousStaticTemplateUuid: reconstructedStaticTemplate?.uuid || '',
      isHydrated: true
    };

    // Save to localStorage after successful template load
    saveStateToStorage(newState);
    return newState;
  }),
  on(TemplateActions.loadDynamicTemplateSuccess, (state, { slug, dynamicTemplate }) => {
    const existingSlugData = state.slugs[slug] || {
      staticTemplate: undefined,
      dynamicTemplate: undefined,
      customSlug: undefined,
      loaded: false,
      lazyLoaded: false,
      lazyLoadedData: null
    };

    // Ensure template is properly reconstructed if needed
    const reconstructedDynamicTemplate = dynamicTemplate instanceof CustomTemplate 
      ? dynamicTemplate 
      : reconstructCustomTemplate(dynamicTemplate);

    const newState = {
      ...state,
      slugs: {
        ...state.slugs,
        [slug]: { 
          ...existingSlugData,
          dynamicTemplate: reconstructedDynamicTemplate
        }
      },
      previousDynamicTemplateUuid: reconstructedDynamicTemplate?.uuid || '',
      isHydrated: true
    };

    // Save to localStorage after successful template load
    saveStateToStorage(newState);
    return newState;
  }),
  on(TemplateActions.loadCustomSlugSuccess, (state, { slug, customSlug }) => {
    const existingSlugData = state.slugs[slug] || {
      staticTemplate: undefined,
      dynamicTemplate: undefined,
      customSlug: undefined,
      loaded: false,
      lazyLoaded: false,
      lazyLoadedData: null
    };

    // Reconstruct CustomSlug instance to ensure methods are available
    const reconstructedCustomSlug = customSlug instanceof CustomSlug 
      ? customSlug 
      : new CustomSlug(
          (customSlug as any).slug,
          (customSlug as any).static_template_uuid,
          (customSlug as any).dynamic_template_uuid,
          (customSlug as any).id,
          (customSlug as any).uuid,
          (customSlug as any).isDevelopment
        );

    const newState = {
      ...state,
      slugs: {
        ...state.slugs,
        [slug]: { 
          ...existingSlugData,
          customSlug: reconstructedCustomSlug
        }
      },
      isHydrated: true
    };

    // Save to localStorage after successful slug load
    saveStateToStorage(newState);
    return newState;
  }),
  on(TemplateActions.loadPageDataFailure, (state, { error }) => {
    return {
      ...state,
      error,
      isHydrated: true
    };
  }),
  on(TemplateActions.loadPageDataSuccess, (state, { slug, templatesChanged, hasCustomSlug, hasTemplates, data }) => {
    const existingSlugData = state.slugs[slug] || {
      staticTemplate: undefined,
      dynamicTemplate: undefined,
      customSlug: undefined,
      loaded: false,
      lazyLoaded: false,
      lazyLoadedData: null
    };

    const newState = {
      ...state,
      slugs: {
        ...state.slugs,
        [slug]: {
          ...existingSlugData,
          loaded: true,
          lazyLoadedData: data || existingSlugData.lazyLoadedData
        }
      },
      isHydrated: true
    };

    // Save to localStorage after successful page data load
    if (hasTemplates || hasCustomSlug) {
      saveStateToStorage(newState);
    }
    
    return newState;
  }),
  on(TemplateActions.loadPageDataUnchanged, (state, { slug }) => {
    const existingSlugData = state.slugs[slug] || {
      staticTemplate: undefined,
      dynamicTemplate: undefined,
      customSlug: undefined,
      loaded: false,
      lazyLoaded: false,
      lazyLoadedData: null
    };

    return {
      ...state,
      slugs: {
        ...state.slugs,
        [slug]: {
          ...existingSlugData,
          loaded: true
        }
      },
      isHydrated: true
    };
  }),
  on(TemplateActions.lazyLoadPageData, (state, { slug, staticTemplate, dynamicTemplate, customSlug, data }) => {
    const existingSlugData = state.slugs[slug] || {
      staticTemplate: undefined,
      dynamicTemplate: undefined,
      customSlug: undefined,
      loaded: false,
      lazyLoaded: false,
      lazyLoadedData: null
    };

    // Reconstruct CustomSlug instance if provided
    const reconstructedCustomSlug = customSlug 
      ? (customSlug instanceof CustomSlug 
          ? customSlug 
          : new CustomSlug(
              (customSlug as any).slug,
              (customSlug as any).static_template_uuid,
              (customSlug as any).dynamic_template_uuid,
              (customSlug as any).id,
              (customSlug as any).uuid,
              (customSlug as any).isDevelopment
            ))
      : existingSlugData.customSlug;

    // Reconstruct CustomTemplate instances if provided
    const reconstructedStaticTemplate = staticTemplate 
      ? (staticTemplate instanceof CustomTemplate 
          ? staticTemplate 
          : reconstructCustomTemplate(staticTemplate))
      : existingSlugData.staticTemplate;

    const reconstructedDynamicTemplate = dynamicTemplate 
      ? (dynamicTemplate instanceof CustomTemplate 
          ? dynamicTemplate 
          : reconstructCustomTemplate(dynamicTemplate))
      : existingSlugData.dynamicTemplate;

    const newState = {
      ...state,
      slugs: {
        ...state.slugs,
        [slug]: {
          ...existingSlugData,
          staticTemplate: reconstructedStaticTemplate,
          dynamicTemplate: reconstructedDynamicTemplate,
          customSlug: reconstructedCustomSlug,
          lazyLoaded: true,
          loaded: false,
          lazyLoadedData: data || existingSlugData.lazyLoadedData
        }
      },
      isHydrated: true
    };

    // Save to localStorage after lazy load
    saveStateToStorage(newState);
    return newState;
  }),
  on(TemplateActions.setCurrentMode, (state, { mode }) => ({
    ...state,
    currentMode: mode,
    isHydrated: true
  }))
);

export interface AppState {
  template: TemplateState;
  auth: AuthState;
  data: DataState;
}
